/**
 * Single place for “file vertex number → scene length in cm”.
 * Scene convention: socket / scan mesh live in **centimeters** (1 world unit = 1 cm).
 *
 * - **scan**: BK meshes are millimeters by default → bake with ×0.1 into cm scene.
 * - **asset** (ladder, foot, etc.): vertices interpreted as mm/cm/**m (default)** → scale into cm scene.
 */

/** @typedef {'mm'|'cm'|'m'} AssetVertexLengthUnit */
/** @typedef {'mm'|'cm'|'m'|'inches'} ScanVertexLengthUnit */
/** @typedef {'mm'|'cm'|'m'|'inches'} VertexLengthUnit */

/** Scene target when baking scans: 1 world unit = 1 cm. */
export const SCENE_VERTEX_UNIT = /** @type {const} */ ('cm');

/** BK / manufacturing scans arrive in millimeters unless the user overrides. */
export const DEFAULT_SCAN_VERTEX_UNIT = /** @type {const} */ ('mm');

/** Default for non-scan assets: one file unit = one meter. */
export const DEFAULT_ASSET_VERTEX_UNIT = /** @type {const} */ ('m');

/** Internal weight for {@link SCENE_VERTEX_UNIT}; divide file weights by this for real multipliers. */
export const SCENE_CM_REFERENCE_WEIGHT = 10;

const ASSET_UNITS = new Set(['mm', 'cm', 'm']);

/**
 * @param {string} unit
 * @returns {unit is AssetVertexLengthUnit}
 */
export function isAssetVertexLengthUnit(unit) {
  return ASSET_UNITS.has(unit);
}

/**
 * Relative weight per file unit (not the final multiplier — see {@link fileUnitToSceneCmMultiplier}).
 *
 * @param {VertexLengthUnit|string} unit
 * @param {{ allowInches?: boolean }} [opts]
 * @returns {number}
 */
export function fileVertexUnitToSceneCmScale(unit, opts = {}) {
  const { allowInches = false } = opts;
  switch (unit) {
    case 'mm':
      return 1;
    case 'cm':
      return SCENE_CM_REFERENCE_WEIGHT;
    case 'm':
      return 1000;
    case 'inches':
      return allowInches ? 2.54 : 1;
    default:
      return 1;
  }
}

/**
 * Multiply raw file coordinates by this to get scene lengths in centimeters.
 *
 * @param {VertexLengthUnit|string} fileUnit
 * @param {{ allowInches?: boolean }} [opts]
 * @returns {number}
 */
export function fileUnitToSceneCmMultiplier(fileUnit, opts = {}) {
  return (
    fileVertexUnitToSceneCmScale(fileUnit, opts) / fileVertexUnitToSceneCmScale(SCENE_VERTEX_UNIT)
  );
}

/**
 * @param {AssetVertexLengthUnit|string} [vertexUnit]
 * @returns {AssetVertexLengthUnit}
 */
export function normalizeAssetVertexUnit(vertexUnit) {
  if (vertexUnit && isAssetVertexLengthUnit(vertexUnit)) return vertexUnit;
  return DEFAULT_ASSET_VERTEX_UNIT;
}

/**
 * Resolve scan unit when UI/store says `auto` or nothing is passed.
 *
 * @param {string|null|undefined} unit
 * @returns {ScanVertexLengthUnit}
 */
export function resolveScanVertexUnit(unit) {
  if (!unit || unit === 'auto') return DEFAULT_SCAN_VERTEX_UNIT;
  if (unit === 'mm' || unit === 'cm' || unit === 'm' || unit === 'inches') return unit;
  return DEFAULT_SCAN_VERTEX_UNIT;
}

/**
 * Multiplier to apply to vertex coordinates (or object scale) so lengths match the cm scene.
 *
 * @param {{ role: 'scan', vertexUnit: ScanVertexLengthUnit|string, manualScale?: number } | { role: 'asset', vertexUnit?: AssetVertexLengthUnit|string, manualScale?: number }} params
 * @returns {number}
 */
export function getVertexScaleToSceneCm(params) {
  const manualScale = params.manualScale ?? 1;
  if (params.role === 'scan') {
    const base = fileUnitToSceneCmMultiplier(resolveScanVertexUnit(params.vertexUnit), {
      allowInches: true,
    });
    return base * manualScale;
  }
  const u = normalizeAssetVertexUnit(params.vertexUnit);
  const base = fileUnitToSceneCmMultiplier(u, { allowInches: false });
  return base * manualScale;
}
